import React from 'react';

import get from 'lodash.get';
import type { NumberFormatValues, SourceInfo } from 'react-number-format';
import NumberFormat from 'react-number-format';

import type { UIOnChangeFn } from '../../@types/types';
import { currencyFormatter } from '../helpers/formatters';
import Input from './Input';
import type { InputFieldProps } from './InputField';
import InputField from './InputField';
import InputReadOnly from './InputReadOnly';

type CurrencyValue = { amount: number; currency: string };

interface InputCurrencyProps extends InputFieldProps {
  item?: { properties?: { amount?: { maximum: number; minimum: number } } };
  value?: Partial<CurrencyValue>;
  isReadOnly?: boolean;
  defaultValue?: CurrencyValue;
  onChange: UIOnChangeFn<CurrencyValue>;
}

const InputCurrency: React.FC<InputCurrencyProps> = ({
  className,
  defaultValue,
  descriptionText,
  errors,
  id,
  isReadOnly,
  isRequired,
  labelText,
  name = 'currency',
  noErrorLabel,
  onChange,
  value = {},
  item,
}) => {
  const currency = 'USD';
  const amount = get(value, 'amount');

  const handleChange = ({ floatValue }: NumberFormatValues, _sourceInfo: SourceInfo) => {
    if (onChange) {
      onChange(floatValue || floatValue === 0 ? { amount: floatValue, currency } : { amount: 0, currency }, name);
    }
  };

  return (
    <InputField
      className={className}
      descriptionText={descriptionText}
      errors={errors}
      id={id || name}
      isRequired={isRequired}
      labelText={labelText}
      name={name}
      noErrorLabel={noErrorLabel}
    >
      {!isReadOnly && (
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center justify-center w-8">$</div>

          <NumberFormat
            allowNegative={false}
            aria-describedby={descriptionText ? `${name}Description` : undefined}
            className="aui-input aui-input-currency"
            customInput={Input}
            decimalScale={0}
            defaultValue={defaultValue && defaultValue.amount}
            errors={errors}
            fixedDecimalScale
            id={name}
            isNumericString
            maxLength={22}
            name={name}
            onValueChange={handleChange}
            thousandSeparator
            value={amount}
            isRequired={isRequired}
            isAllowed={({ floatValue, value }: NumberFormatValues) => {
              if (value === '' || (!item?.properties?.amount?.maximum && !item?.properties?.amount?.minimum)) {
                return true;
              }

              if (item?.properties?.amount?.maximum) {
                return floatValue ? floatValue <= item?.properties?.amount?.maximum : false;
              }

              if (item?.properties?.amount?.minimum) {
                return floatValue ? floatValue >= item?.properties?.amount?.minimum : false;
              }

              return false;
            }}
          />
        </div>
      )}

      {isReadOnly && (
        <InputReadOnly
          key={JSON.stringify(value)}
          id={id || name}
          name={name}
          errors={errors}
          value={currencyFormatter(value) || undefined}
        />
      )}
    </InputField>
  );
};

export default InputCurrency;
